<template>
  <div>
    <span
      :class="totalSharedTrips ? 'text-primary cursor-pointer' : ''"
      @click="selectUserTrips"
    >
      <!-- Show loading spinner when loading, else show the totalSharedTrips count -->
      <span v-if="loading">
        <v-progress-circular indeterminate color="primary" size="24"></v-progress-circular>
      </span>
      <span v-else>
        {{ totalSharedTrips }}
      </span>
    </span>
    <SharedTripsModal v-model="isSharedTripsModalOpen"
                      :row="row"
                      :enable-urm-roles="enableUrmRoles"
                      :shared-trips="sharedTrips"/>
  </div>
</template>

<script lang="ts">
import SharedContact from '@/models/dto/SharedContact'
import SharedTripsModal from '@/components/SharedTripsModal.vue'
import { Vue, Prop, Component } from 'vue-property-decorator'
import { EventBus } from '@/utils/eventBus'
import SharedTrip from "@/models/dto/SharedTrip";
import user from "@/services/user";

@Component({
  components: {
    SharedTripsModal,
  },
})
export default class SharedContactsSharedTrips extends Vue {
  @Prop({ required: true }) readonly row: SharedContact
  @Prop({ required: true }) readonly enableUrmRoles: boolean

  isSharedTripsModalOpen: boolean = false
  sharedTrips: SharedTrip[] = []
  loading: boolean = false  // Loading state for fetching trips

  get totalSharedTrips(): number {
    return this.sharedTrips.length  // Use the length of the fetched shared trips
  }

  mounted(): void {
    EventBus.$on('update-shared-contacts', this.fetchAndFilterTrips)
    this.fetchAndFilterTrips()
  }

  selectUserTrips(): void {
    if (this.totalSharedTrips > 0) {
      this.isSharedTripsModalOpen = true
    }
  }

  async fetchAndFilterTrips(): Promise<void> {
    this.loading = true  // Start loading when fetching
    try {
      const response = await user.sharedTrips(this.row.userId)
      this.sharedTrips = this.filteredTrips(response.data)  // Filter trips after fetching
    } catch (error) {
      console.error('Error fetching trips:', error)
    } finally {
      this.loading = false  // Stop loading once the trips are fetched
    }
  }

  filteredTrips(trips: SharedTrip[]): SharedTrip[] {
    const filteredByUrmEnabled = trips.filter(item => item.backedByUrm === this.enableUrmRoles)
    if (!this.enableUrmRoles) {
      return filteredByUrmEnabled
    }

    // Group trips by quoteId
    const groupedTrips = filteredByUrmEnabled.reduce((groups, trip) => {
      if (!groups[trip.quoteId]) {
        groups[trip.quoteId] = []
      }
      groups[trip.quoteId].push(trip)
      return groups
    }, {} as Record<number, SharedTrip[]>)

    const uniqueTrips: SharedTrip[] = []

    // Apply filtering logic to grouped trips
    Object.values(groupedTrips).forEach(group => {
      if (group.length === 1) {
        // Keep the single trip in the group
        uniqueTrips.push(group[0])
      } else {
        const nullReservationManagedId = group.filter(trip => trip.reservationManagedId === null)
        const nonNullReservationManagedId = group.filter(trip => trip.reservationManagedId !== null)

        if (nonNullReservationManagedId.length > 0) {
          // Keep all trips with a non-null reservationManagedId
          uniqueTrips.push(...nonNullReservationManagedId)
        } else {
          // If all trips have null reservationManagedId, keep only one
          uniqueTrips.push(nullReservationManagedId[0])
        }
      }
    })

    return uniqueTrips
  }
}
</script>
